import React, { ReactElement, Suspense, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleChevronLeftIcon,
  DoubleChevronRightIcon,
} from '@adsk/alloy-react-icon';
import { useTheme } from '@adsk/alloy-react-styles';
import { StylableComponent, stylePropType } from '@adsk/alloy-react-theme';

import useTableSize from '../hooks/useTableSize';
import { useTranslation } from '../i18n';
import { Themeable } from '../types';
import PaginationButton from './PaginationButton';

type PaginationFooterProps = StylableComponent<HTMLDivElement> &
  Themeable & {
    canPreviousPage: boolean;
    canNextPage: boolean;
    pageCount: number;
    setPageIndex: (pageIndex: number) => void;
    pageIndex: number;
    pageSize: number;
    totalDataCount: number;
    text?: string;
  };

const PaginationFooterContent = ({
  canPreviousPage,
  canNextPage,
  setPageIndex,
  pageIndex,
  pageSize,
  pageCount,
  totalDataCount,
  tableSize,
  style,
  className,
  text,
}: PaginationFooterProps): ReactElement => {
  const tableSizeTheme = useTableSize(tableSize);
  const theme = useTheme();
  const pagination = theme.tokens?.component?.table?.pagination;
  const { t } = useTranslation();

  const displayText = useMemo(() => {
    return (
      text ||
      t('SHOWING', {
        itemRangeStart: pageSize * pageIndex + 1,
        itemRangeEnd: Math.min(pageSize * pageIndex + pageSize, totalDataCount),
        itemCount: totalDataCount,
      })
    );
  }, [text, t, pageSize, pageIndex, totalDataCount]);

  return (
    <div
      className={className}
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: tableSizeTheme.rowHeight,
          padding: `0 ${tableSizeTheme.cellPadding}px`,
          ...pagination?._,
          position: 'sticky',
          bottom: 0,
          left: 0,
          zIndex: 1,
        },
        style,
      ]}
    >
      <div css={pagination?.text}>{displayText}</div>
      <div css={[{ display: 'flex', alignItems: 'center' }]}>
        <PaginationButton
          data-testid="pagination-first-page"
          disabled={!canPreviousPage}
          renderIcon={useCallback(
            () => (
              <DoubleChevronLeftIcon />
            ),
            [],
          )}
          onClick={() => setPageIndex(0)}
          style={{ marginRight: pagination?.navButton?.margin }}
          aria-label={t('PAGINATION_BUTTON_FIRST_PAGE')}
        />
        <PaginationButton
          data-testid="pagination-previous-page"
          aria-label={t('PAGINATION_BUTTON_PREV_PAGE')}
          disabled={!canPreviousPage}
          renderIcon={useCallback(
            () => (
              <ChevronLeftIcon />
            ),
            [],
          )}
          onClick={() => setPageIndex(pageIndex - 1)}
        />
        <div
          css={[
            {
              ...pagination?.pageInfo,
              minWidth: '8ch',
              textAlign: 'center',
              whiteSpace: 'nowrap',
            },
          ]}
        >
          {t('PAGE_COUNT', {
            currentPage: pageIndex + 1,
            totalPages: pageCount,
          })}
        </div>
        <PaginationButton
          data-testid="pagination-next-page"
          aria-label={t('PAGINATION_BUTTON_NEXT_PAGE')}
          disabled={!canNextPage}
          renderIcon={useCallback(
            () => (
              <ChevronRightIcon />
            ),
            [],
          )}
          onClick={() => setPageIndex(pageIndex + 1)}
        />
        <PaginationButton
          data-testid="pagination-last-page"
          aria-label={t('PAGINATION_BUTTON_LAST_PAGE')}
          disabled={!canNextPage}
          renderIcon={useCallback(
            () => (
              <DoubleChevronRightIcon />
            ),
            [],
          )}
          onClick={() => setPageIndex(pageCount - 1)}
          style={{ marginLeft: pagination?.navButton?.margin }}
        />
      </div>
    </div>
  );
};

const PaginationFooter = (props: PaginationFooterProps): ReactElement => {
  return (
    <Suspense fallback={null}>
      <PaginationFooterContent {...props} />
    </Suspense>
  );
};

PaginationFooter.propTypes = {
  canPreviousPage: PropTypes.bool.isRequired,
  canNextPage: PropTypes.bool.isRequired,
  pageCount: PropTypes.number.isRequired,
  setPageIndex: PropTypes.func.isRequired,
  state: PropTypes.shape({
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    totalDataCount: PropTypes.number.isRequired,
  }),
  style: stylePropType,
};

export default PaginationFooter;
